import _ from "lodash";
import update from "immutability-helper";
import React from "react";

import ACLStore from "lib/aclStore";
import {
  generateRandomId,
  getMemoizedFunction,
  getMemoizedLangFunction,
  isUserTypeParent,
  isUserTypeStaff,
} from "Utils";
import {
  ENABLE_ASSESSMENTS_IN_PYP,
  ENABLE_AI_TUTOR,
  OPEN_AND_CREATE_ASSIGNMENTS_IN_EDIT_MODE,
  ENABLE_GRADE_WISE_ASSESSMENT,
} from "Constants/permissionConstants";
import { ASSESSMENT_TYPE_WORKSHEET } from "Worksheet/modules/WorksheetConstants";
import {
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_UBD,
  CREATE_AI_TUTOR,
  ASSESSMENT_TYPE_AI_TUTOR,
  ASSESSMENT_TYPE_QUICK_TASK,
} from "Constants/stringConstants";

import {
  ImportOutlined,
  UnitPlanOutlined,
  LeLibraryOutlined,
  JournalOutlined,
  SubsectionListOutlined,
  LeOutlined,
  FaOutlined,
  SaOutlined,
  AssessmentAddOutlined,
  WorksheetClipboardOutlined,
  LinkOutlined,
  NoteOutlined,
  UploadOutlined,
  GoogleDriveColored,
  OneDriveColored,
  DeviceDesktopOutlined,
  SectionListOutlined,
  AiOutlined,
  PencilLineOutlined,
  DeleteOutlined,
  DownloadOutlined,
  SwitchArrowsOutlined,
  AddToLibraryOutlined,
  CaretOpenedFilled,
  CaretClosedFilled,
  PrintOutlined,
  ReloadArrowOutlined,
  LockOpenOutlined,
  LockFilled,
  ShareCurveOutlined,
  MarkAsReadOutlined,
  TickCircleOutlined,
  TickCircleOpenOutlined,
  UserTickOutlined,
  DotsHorizontalOutlined,
  EyeOutlined,
} from "@toddle-edu/ds-icons";
import { Avatar } from "@toddle-edu/ds-web";

import { splitAssessmentAndResourceTypesMemoized } from "Utils/EntityFlowUtils";
import { getGroupOrAssessmentTypes } from "AppComponents/Gradebook/modules/GradebookGraphqlHelpers";
import { getUnitFlowItemFromCache } from "./GraphqlHelper";
import { getIsGradingPeriodUnlocked } from "GradingSetup/modules/Utils";
import {
  MAX_DEPTH,
  MAX_FOLDER_DEPTH,
} from "UIComponents/NestedAccordion/constants";
import { getUserInfo, isToddleLibraryRoute } from "Login/modules/LoginModule";
import {
  LOCKING_ACTIONS,
  getFolderLockingOptions,
  getSyncStatusCompData,
  getIsImportFromToddleLibraryEnabled,
} from "CourseFlow/modules/Utils";
import { getIsProSchool } from "UnitPlans/modules/Utils";
import classes from "../../components/UnitFlow.scss";
import {
  getIsBlueprintSyncHistoryV2Enabled,
  getIsSyncAndLockAllowed,
} from "LearningCourse/modules/LearningCourseUtils";
import { isSchoolLibraryEnabled } from "SchoolLibrary/modules/Utils";
import { isToddleLibraryEnabled } from "ToddleLibrary/modules/ToddleLibraryUtils";
import { getAssessmentTemplatesFromCache } from "ChildrenPage/modules/ChildrenPageGraphqlHelpers";

import {
  getIsAssessmentTypeConfigurationEnabled,
  getIsUniversalParentEnabled,
} from "Utils/ACLStoreUtils";

export const DEFAULT_FILTERS = {
  selectedItemTypes: "ALL",
  searchText: "",
  selectedTaskTypeIds: "ALL",
};

export const PUSHER_EVENTS = {
  FEED_UPDATE: "courseFlowFeedUpdate",
  RESOURCE_UPDATE: "courseFlowResourceUpdate",
};

export const isUnitFlowRevampEnabled = () => !isToddleLibraryRoute();

const LEARNING_COURSE_FLOW_ITEM_CATEGORIES = ["FOLDER", "FILE", "ASSESSMENT"];

const getUnitFlowFeedVariables = ({
  unitPlanId,
  filters,
  userInfo: _userInfo,
  curriculumType,
  resourcesOnly = false,
  assessmentsOnly = false,
  availableCategories = [],
  learningCourseId,
  classId,
}) => {
  const userInfo = _userInfo || getUserInfo();
  const userType = userInfo.user_type;
  const isUserTeacher = isUserTypeStaff({ userType });
  const isUserParent = isUserTypeParent({ userType });
  const isAssessmentTypeConfigurationEnabled = getIsAssessmentTypeConfigurationEnabled();

  const parentId = isUserTeacher ? learningCourseId : classId;

  const unitFlowFilters = _.mapValues(filters, (value, key) => {
    const isAllOption = _.isEqual(value, "ALL") && key != "searchText";
    return isAllOption ? [] : value;
  });

  const {
    selectedItemTypes = [],
    selectedAddedBy = [],
    searchText = "",
    selectedTaskTypeIds = [],
  } = unitFlowFilters;

  const createdBy = (
    _.isArray(selectedAddedBy)
      ? _.includes(selectedAddedBy, "ME")
      : selectedAddedBy == "ME"
  )
    ? userInfo?.id
    : [];

  const {
    selectedAssessmentTypes,
    selectedResourceTypes,
  } = splitAssessmentAndResourceTypesMemoized({ selectedItemTypes });

  let assessmentFilters = {};

  if (isAssessmentTypeConfigurationEnabled) {
    // When FF is on, don't include assessmentType and groupTypes
    assessmentFilters = {};
  } else {
    // When FF is off, use existing logic with assessmentTypes and groupTypes
    const { assessmentTypes = [], groupTypes = [] } = getGroupOrAssessmentTypes(
      {
        curriculumType,
        selectedAssessmentTypes,
      }
    );

    assessmentFilters = {
      assessmentType: assessmentTypes,
      groupTypes,
    };
  }

  let itemTypes = [];

  switch (true) {
    case resourcesOnly:
      itemTypes = ["FILE"];
      break;
    case assessmentsOnly:
      itemTypes = ["ASSESSMENT"];
      break;
    default: {
      if (!_.isEmpty(availableCategories)) {
        itemTypes = _.filter(LEARNING_COURSE_FLOW_ITEM_CATEGORIES, category =>
          availableCategories.includes(category)
        );
      } else {
        itemTypes = LEARNING_COURSE_FLOW_ITEM_CATEGORIES;
      }
    }
  }

  const queryFilters = {
    unitPlanId,
    searchText,
    itemTypes,
    attachmentTypes: selectedResourceTypes,
    createdBy,
    assessmentFilters,
    isHierarchicalFeed: !(resourcesOnly || assessmentsOnly),
  };

  if (isUserParent) {
    queryFilters.studentIds = [userInfo.childID];
  }

  if (!isUserTeacher && !_.isEmpty(classId)) {
    queryFilters.classIds = [classId];
  }

  // Add taskTypeIds when FF is enabled
  const taskTypeIds = isAssessmentTypeConfigurationEnabled
    ? selectedTaskTypeIds
    : undefined;

  return {
    parentId,
    parentType: isUserTeacher ? "LEARNING_COURSE" : "COURSE",
    filters: queryFilters,
    orderBy: "DISPLAY_SEQUENCE",
    syncedBluePrintItemFilters: {
      learningCourseId: parentId,
    },
    ...(isAssessmentTypeConfigurationEnabled && !_.isEmpty(taskTypeIds)
      ? { taskTypeIds }
      : {}),
  };
};

export const getUnitFlowFeedVariablesMemoized = getMemoizedFunction(
  getUnitFlowFeedVariables
);

const getUnitPlanOutlineFeedVariables = ({
  unitPlanId,
  filters,
  userInfo,
  curriculumType,
  learningCourseId,
  classId,
}) => {
  const feedVariables = getUnitFlowFeedVariables({
    unitPlanId,
    filters,
    userInfo,
    curriculumType,
    learningCourseId,
    classId,
  });

  const isUniversalParentEnabled = getIsUniversalParentEnabled();

  return update(feedVariables, {
    filters: {
      $merge: {
        maxDepth: MAX_FOLDER_DEPTH,
        ...(isUniversalParentEnabled && { includeAllItemTypes: true }),
      },
    },
  });
};

export const getUnitPlanOutlineFeedVariablesMemoized = getMemoizedFunction(
  getUnitPlanOutlineFeedVariables
);

/**
 * This function receives data in old format of class flow and returns it in new
 * unit flow revamped format so that common components across class and unit flow
 * can be reused
 */
export const getUpdatedEntityFlowStructure = ({ feedData }) => {
  return _.map(feedData, node => {
    const childrenV2 = _.get(node, "childrenV2", []);

    const item = _.get(node, "item", {});
    const isUnitResource = _.has(item, "attachmentType");
    const hasAttachment = _.has(item, "attachment") || isUnitResource;
    const attachment = isUnitResource ? item : _.get(item, "attachment", {});

    const updatedAttachment = hasAttachment
      ? {
          ...attachment,
          type: attachment?.attachmentType,
          title: attachment?.attachmentTitle,
        }
      : null;

    const updatedItem = hasAttachment ? updatedAttachment : item;

    const resourceType = _.get(node, "resourceType", "");

    return {
      ...node,
      childrenV2: { edges: _.map(childrenV2, child => ({ node: child })) },
      attachment: updatedAttachment,
      item: updatedItem,
      resourceType,
    };
  });
};

const getDisplaySettings = ({
  t,
  isBlueprintCourse,
  isUserTeacher,
  isTeacherCourseAssociationEnabled,
  isCourseAssociatedWithBlueprint,
}) => {
  const showBlueprintLockIcon =
    !isBlueprintCourse &&
    isUserTeacher &&
    isTeacherCourseAssociationEnabled &&
    isCourseAssociatedWithBlueprint;

  return [
    {
      key: "show_outline",
      label: t("classMaterials:show_outline"),
      type: "checkbox",
      hasDivider: true,
    },
    {
      key: "collapse_sections",
      isItemGroup: true,
      options: [
        {
          key: "collapse_all_sections",
          label: t("classMaterials:collapse_all_sections"),
          type: "radio",
        },
        {
          key: "expand_all_sections",
          label: t("classMaterials:expand_all_sections"),
          type: "radio",
        },
      ],
    },
    ...(isUserTeacher
      ? [
          {
            key: "default_item_placement",
            label: t("classMaterials:default_item_placement"),
            isItemGroup: true,
            options: [
              {
                key: "add_to_top",
                label: t("classMaterials:add_to_top"),
                type: "radio",
              },
              {
                key: "add_to_bottom",
                label: t("classMaterials:add_to_bottom"),
                type: "radio",
              },
            ],
          },
        ]
      : []),
    {
      key: "task_resource_icon",
      label: t("classMaterials:task_resource_icon"),
      isItemGroup: true,
      options: [
        {
          key: "thumbnail_image",
          label: t("classMaterials:thumbnail_image"),
          type: "radio",
        },
        {
          key: "task_resource_type",
          label: t("classMaterials:task_resource_type"),
          type: "radio",
        },
      ],
    },
    {
      key: "task_details",
      label: t("classMaterials:task_details"),
      isItemGroup: true,
      options: [
        ...(showBlueprintLockIcon
          ? [
              {
                key: "show_blueprint_lock",
                label: t("course:show_blueprint_lock_indicator"),
                type: "checkbox",
              },
            ]
          : []),
        {
          key: "grading_period",
          label: t("common:grading_period"),
          type: "checkbox",
        },
        {
          key: "assessment_category",
          label: t("classMaterials:assessment_category"),
          type: "checkbox",
        },
      ],
    },
  ];
};

export const getDisplaySettingsMemoized = getMemoizedLangFunction(
  getDisplaySettings
);

const getDepthWiseSectionOptions = ({ depth, isFolder }) => {
  // this option is only available for sections
  // it creates a new section at same level of current section after its children
  const parallelSectionOption = {
    label: "classMaterials:add_section",
    key: "create_parallel_section",
    icon: <SectionListOutlined />,
  };

  const sectionOption = {
    label: "classMaterials:add_section",
    key: "create_section",
    icon: <SectionListOutlined />,
  };

  const subsectionOption = {
    label: "classMaterials:add_subsection",
    key: "create_subsection",
    icon: <SubsectionListOutlined />,
  };

  /* 'depth' here denotes the depth at which the new item will be createdAt
   * if 'isFolder' is true, that means the trigger element is a section
   * 'isCollapsed' is true then the folder element is 1 level above depth, otherwise same level
   */

  /**
   * Depth 1
   * Can only create sections
   */

  if (depth === 1) {
    return [sectionOption];
  }

  /**
   * Depth 2
   * Files:
   ** Can only create sections under files
   *
   * Folders:
   ** Section + subsection
   */

  if (depth === 2) {
    if (isFolder) {
      return [subsectionOption];
    }

    return [sectionOption];
  }

  /**
   * Depth above 2 till max folder depth
   * Files:
   ** Can only create sections below files
   *
   * Folders:
   ** Both parallel and subsections can be created
   */
  if (_.inRange(depth, 3, MAX_DEPTH)) {
    if (isFolder) {
      return [parallelSectionOption, subsectionOption];
    }

    return [sectionOption];
  }

  // Restrict sections above max folder depth
  if (depth > MAX_FOLDER_DEPTH && !isFolder) {
    return [];
  }

  // Can create a parallel section at max depth
  if (depth === MAX_DEPTH && isFolder) {
    return [parallelSectionOption];
  }

  return [];
};

export const canCreateAssessmentInEditMode = () =>
  ACLStore.can(OPEN_AND_CREATE_ASSIGNMENTS_IN_EDIT_MODE);

// If there is change please also update in combined library (rightPane of unitFlow)
const getAddDropDownOptions = ({
  t,
  curriculumType,
  depth,
  isFolder,
  isTermsExists,
  excludeFolderOptions = false,
  isUnlockedTermExistsForCurrentLearningCourse = true,
}) => {
  const isPYPAssessmentEnabled = ACLStore.can(ENABLE_ASSESSMENTS_IN_PYP);

  const canCreateAssessments = _.includes(
    [
      CURRICULUM_TYPE_DP,
      CURRICULUM_TYPE_MYP,
      ...(isPYPAssessmentEnabled ? [CURRICULUM_TYPE_PYP] : []),
    ],
    curriculumType
  );

  const canCreateWorksheets = ACLStore.can("FeatureFlag:ClassroomWorksheet");
  const canCreateAIChatTask = ACLStore.can(ENABLE_AI_TUTOR);
  const isMapleBearLikeOrg = ACLStore.can("FeatureFlag:IsMapleBearLikeOrg");

  const showSchoolLibraryOption = isSchoolLibraryEnabled();
  const showToddleLibraryOption = isToddleLibraryEnabled();
  const isImportFromToddleLibrary = getIsImportFromToddleLibraryEnabled();

  return _.compact([
    {
      label: "common:create",
      key: "CREATE",
      isItemGroup: true,
      options: [
        ...(excludeFolderOptions
          ? []
          : getDepthWiseSectionOptions({ depth, isFolder })),
        {
          label: "common:le_label_plural",
          key: "CREATE_LE",
          icon: <LeOutlined />,
          disabled:
            !isTermsExists || !isUnlockedTermExistsForCurrentLearningCourse,
          disabledTooltip: !isTermsExists
            ? t(
                "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
                { label: t("common:le_label_plural") }
              )
            : t("course:item_cannot_be_added_due_to_locked_grading_periods"),
        },
        canCreateAssessments && {
          label: "common:formative_assessments",
          key: "CREATE_FA",
          icon: <FaOutlined />,
          disabled:
            !isTermsExists || !isUnlockedTermExistsForCurrentLearningCourse,
          disabledTooltip: !isTermsExists
            ? t(
                "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
                { label: t("common:formative_assessments") }
              )
            : t("course:item_cannot_be_added_due_to_locked_grading_periods"),
        },
        canCreateAssessments && {
          label: "common:summative_assessments",
          key: "CREATE_SA",
          icon: <SaOutlined />,
          disabled:
            !isTermsExists || !isUnlockedTermExistsForCurrentLearningCourse,
          disabledTooltip: !isTermsExists
            ? t(
                "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
                { label: t("common:summative_assessments") }
              )
            : t("course:item_cannot_be_added_due_to_locked_grading_periods"),
        },
        curriculumType === CURRICULUM_TYPE_UBD && {
          label: "common:assessment",
          key: "CREATE_ASSESSMENT",
          icon: <AssessmentAddOutlined />,
          disabled:
            !isTermsExists || !isUnlockedTermExistsForCurrentLearningCourse,
          disabledTooltip: !isTermsExists
            ? t(
                "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
                { label: t("common:assessment") }
              )
            : t("course:item_cannot_be_added_due_to_locked_grading_periods"),
        },
        canCreateWorksheets && {
          label: "common:worksheet_other",
          key: "CREATE_WORKSHEET",
          value: ASSESSMENT_TYPE_WORKSHEET,
          icon: <WorksheetClipboardOutlined />,
          disabled:
            !isTermsExists || !isUnlockedTermExistsForCurrentLearningCourse,
          disabledTooltip: !isTermsExists
            ? t(
                "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
                { label: t("common:worksheet_other") }
              )
            : t("course:item_cannot_be_added_due_to_locked_grading_periods"),
        },
        canCreateAIChatTask && {
          label: "common:ai_tutor",
          key: CREATE_AI_TUTOR,
          value: ASSESSMENT_TYPE_AI_TUTOR,
          icon: <AiOutlined />,
          disabled:
            !isTermsExists || !isUnlockedTermExistsForCurrentLearningCourse,
          disabledTooltip: !isTermsExists
            ? t(
                "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
                { label: t("common:ai_tutor") }
              )
            : t("course:item_cannot_be_added_due_to_locked_grading_periods"),
        },
        {
          label: "common:note",
          key: "ADD_NOTE",
          icon: <NoteOutlined />,
        },
        {
          label: "common:link",
          key: "ADD_LINK",
          icon: <LinkOutlined />,
        },
        {
          label: "common:upload_from",
          key: "upload_menu",
          isSubMenu: true,
          showDivider: false,
          icon: <UploadOutlined />,
          options: [
            {
              key: "add_google_drive",
              label: "common:google_drive",
              icon: <GoogleDriveColored />,
            },
            {
              key: "add_one_drive",
              label: "common:one_drive",
              icon: <OneDriveColored />,
            },
            {
              key: "upload_device",
              label: "common:device",
              icon: <DeviceDesktopOutlined />,
            },
          ],
        },
        {
          key: "MORE",
          label: "common:more",
          icon: <DotsHorizontalOutlined />,
          isSubMenu: true,
          options: [],
        },
      ],
    },
    {
      label: "common:import_from",
      key: "import_menu",
      isSubMenu: true,
      icon: <ImportOutlined />,
      options: _.compact([
        {
          key: "IMPORT_FROM_OTHER_UNIT",
          label: "unitPlan:other_unit_of_this_academic_year",
          icon: <UnitPlanOutlined />,
          disabled:
            !isTermsExists || !isUnlockedTermExistsForCurrentLearningCourse,
          disabledTooltip: !isTermsExists
            ? t("classRoom:import_from_label_no_grading_periods_tooltip", {
                label: t("common:unit_lowercase"),
              })
            : t(
                "course:items_cannot_be_imported_due_to_locked_grading_periods"
              ),
        },
        showSchoolLibraryOption && {
          key: "IMPORT_LE_FROM_LE_LIBRARY",
          label: "common:learning_experience_and_assessment_library",
          icon: <LeLibraryOutlined />,
          disabled:
            !isTermsExists || !isUnlockedTermExistsForCurrentLearningCourse,
          disabledTooltip: !isTermsExists
            ? t("classRoom:import_from_label_no_grading_periods_tooltip", {
                label: t("common:learning_experience_and_assessment_library"),
              })
            : t(
                "course:items_cannot_be_imported_due_to_locked_grading_periods"
              ),
        },
        !isMapleBearLikeOrg &&
          showToddleLibraryOption &&
          isImportFromToddleLibrary && {
            key: "IMPORT_FROM_TODDLE_LIBRARY",
            label: "common:toddle_library",
            icon: <JournalOutlined />,
            disabled:
              !isTermsExists || !isUnlockedTermExistsForCurrentLearningCourse,
            disabledTooltip: !isTermsExists
              ? t("classRoom:import_from_label_no_grading_periods_tooltip", {
                  label: t("common:toddle_library"),
                })
              : t(
                  "course:items_cannot_be_imported_due_to_locked_grading_periods"
                ),
          },
      ]),
    },
  ]);
};

export const getTemplateIdFromTaskType = (
  taskTypeId,
  gradeId,
  curriculumId,
  isGradeWiseAssessment = false
) => {
  const data = getAssessmentTemplatesFromCache(curriculumId);

  if (_.isEmpty(data)) {
    return null;
  }

  const taskTypes = _.get(data, "taskTypes", []);

  const taskType = _.find(taskTypes, { id: taskTypeId });

  if (_.isEmpty(taskType)) {
    return null;
  }

  const templates = _.get(taskType, "templates", []);

  // Filter templates by grade if grade-wise assessment is enabled and gradeId is provided
  if (isGradeWiseAssessment && gradeId) {
    const filteredTemplates = _.filter(templates, template => {
      const templateGrades = _.get(template, "grades", []);
      return _.some(templateGrades, grade => grade.id === gradeId);
    });

    if (_.isEmpty(filteredTemplates)) {
      return _.get(_.first(templates), "id", null);
    } else {
      return _.get(_.first(filteredTemplates), "id", null);
    }
  }

  // When not filtering by grade, return the first template
  return _.get(_.first(templates), "id", null);
};

const getAddDropDownOptionsV2 = ({
  t,
  curriculumProgramId,
  isTermsExists,
  isUnlockedTermExistsForCurrentLearningCourse = true,
  gradeId,
  depth,
  isFolder,
  excludeFolderOptions = false,
  // Passed in by the caller to avoid a circular dependency between this file
  // and the Utils/EntityFlowUtils barrel.
  getTaskTypeAvatarName,
}) => {
  const isMapleBearLikeOrg = ACLStore.can("FeatureFlag:IsMapleBearLikeOrg");

  const showSchoolLibraryOption = isSchoolLibraryEnabled();
  const showToddleLibraryOption = isToddleLibraryEnabled();
  const isImportFromToddleLibrary = getIsImportFromToddleLibraryEnabled();

  const assessmentTemplatesData = getAssessmentTemplatesFromCache(
    curriculumProgramId
  );
  const taskTypes = _.get(assessmentTemplatesData, "taskTypes", []);

  const sortedTaskTypes = _.sortBy(taskTypes, "displaySequence");

  const filteredTaskTypes = _.filter(
    sortedTaskTypes,
    taskType =>
      !_.includes([ASSESSMENT_TYPE_QUICK_TASK], _.get(taskType, "type", ""))
  );

  const isGradeWiseAssessment = ACLStore.can(ENABLE_GRADE_WISE_ASSESSMENT);
  const isAssessmentTypeConfigurationEnabled = getIsAssessmentTypeConfigurationEnabled();

  const assessmentOptions = _.map(filteredTaskTypes, taskType => {
    const type = _.get(taskType, "type", "");
    const label = _.get(taskType, "label", "");
    const taskTypeId = _.get(taskType, "id", "");
    const color = _.get(taskType, "color", "");

    const templateId = getTemplateIdFromTaskType(
      taskTypeId,
      gradeId,
      curriculumProgramId,
      isGradeWiseAssessment
    );

    const avatarName = getTaskTypeAvatarName({
      type,
      label,
      isAssessmentTypeConfigurationEnabled,
    });

    return {
      label: label,
      key: taskTypeId,
      value: taskTypeId,
      taskType: type,
      templateId,
      icon: (
        <Avatar
          shape="square"
          size="large"
          name={avatarName}
          color={color}
          dsVersion="2.0"
        />
      ),
      disabled: !isTermsExists || !isUnlockedTermExistsForCurrentLearningCourse,
      disabledTooltip: !isTermsExists
        ? t(
            "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
            { label }
          )
        : t("course:item_cannot_be_added_due_to_locked_grading_periods"),
    };
  });

  return _.compact([
    {
      label: "common:create",
      key: "CREATE",
      isItemGroup: true,
      options: [
        ...(excludeFolderOptions
          ? []
          : getDepthWiseSectionOptions({ depth, isFolder })),
        ...assessmentOptions,
        {
          label: "common:note",
          key: "ADD_NOTE",
          icon: <NoteOutlined />,
        },
        {
          label: "common:link",
          key: "ADD_LINK",
          icon: <LinkOutlined />,
        },
        {
          label: "common:upload_from",
          key: "upload_menu",
          isSubMenu: true,
          showDivider: false,
          icon: <UploadOutlined />,
          options: [
            {
              key: "add_google_drive",
              label: "common:google_drive",
              icon: <GoogleDriveColored />,
            },
            {
              key: "add_one_drive",
              label: "common:one_drive",
              icon: <OneDriveColored />,
            },
            {
              key: "upload_device",
              label: "common:device",
              icon: <DeviceDesktopOutlined />,
            },
          ],
        },
        {
          key: "MORE",
          label: "common:more",
          icon: <DotsHorizontalOutlined />,
          isSubMenu: true,
          options: [],
        },
      ],
    },
    {
      label: "common:import_from",
      key: "import_menu",
      isSubMenu: true,
      icon: <ImportOutlined />,
      options: _.compact([
        {
          key: "IMPORT_FROM_OTHER_UNIT",
          label: "unitPlan:other_unit_of_this_academic_year",
          icon: <UnitPlanOutlined />,
          disabled:
            !isTermsExists || !isUnlockedTermExistsForCurrentLearningCourse,
          disabledTooltip: !isTermsExists
            ? t("classRoom:import_from_label_no_grading_periods_tooltip", {
                label: t("common:unit_lowercase"),
              })
            : t(
                "course:items_cannot_be_imported_due_to_locked_grading_periods"
              ),
        },
        showSchoolLibraryOption && {
          key: "IMPORT_LE_FROM_LE_LIBRARY",
          label: "common:learning_experience_and_assessment_library",
          icon: <LeLibraryOutlined />,
          disabled:
            !isTermsExists || !isUnlockedTermExistsForCurrentLearningCourse,
          disabledTooltip: !isTermsExists
            ? t("classRoom:import_from_label_no_grading_periods_tooltip", {
                label: t("common:learning_experience_and_assessment_library"),
              })
            : t(
                "course:items_cannot_be_imported_due_to_locked_grading_periods"
              ),
        },
        !isMapleBearLikeOrg &&
          showToddleLibraryOption &&
          isImportFromToddleLibrary && {
            key: "IMPORT_FROM_TODDLE_LIBRARY",
            label: "common:toddle_library",
            icon: <JournalOutlined />,
            disabled:
              !isTermsExists || !isUnlockedTermExistsForCurrentLearningCourse,
            disabledTooltip: !isTermsExists
              ? t("classRoom:import_from_label_no_grading_periods_tooltip", {
                  label: t("common:toddle_library"),
                })
              : t(
                  "course:items_cannot_be_imported_due_to_locked_grading_periods"
                ),
          },
      ]),
    },
  ]);
};

export const getAddDropDownOptionsMemoized = getMemoizedLangFunction(
  getAddDropDownOptions
);

export const getAddDropDownOptionsV2Memoized = getMemoizedLangFunction(
  getAddDropDownOptionsV2
);

const getMoveToSectionOption = ({
  t,
  itemType,
  itemDepth,
  maxChildDepth = 0,
  sectionsCount,
  isUniversalParentEnabled = false,
}) => {
  if (isUniversalParentEnabled) return [];

  // no need to show move to section option if there are
  // no sections in the feed
  if (sectionsCount === 0) return [];

  const isItemFolder = itemType === "FOLDER";
  if (isItemFolder && itemDepth > MAX_FOLDER_DEPTH) return [];

  let itemStructureDepth = 1;

  if (maxChildDepth > 0) {
    itemStructureDepth += maxChildDepth;
    itemStructureDepth -= itemDepth;
  }

  if (isItemFolder) {
    itemStructureDepth += 1;
  }

  const moveToSectionLabel =
    itemDepth === 1
      ? t("common:move_to_section")
      : t("common:move_to_another_section");

  return [
    {
      key: "MOVE_TO_SECTION",
      label: moveToSectionLabel,
      icon: <SwitchArrowsOutlined />,
      disabled: itemStructureDepth > MAX_FOLDER_DEPTH,
      disabledTooltip: t(
        "common:hierarchy_level_restriction_for_section_with_level",
        { level: MAX_DEPTH }
      ),
    },
  ];
};

const getCardDropdownOptions = ({
  t,
  type,
  attachmentType,
  itemDepth,
  sectionsCount,
  maxChildDepth,
  isExpanded,
  academicTerm,
  assessmentLabel,
  isTermsExists,
  showExpandCollapseAllOption,
  isUnitFlowSharingEnabled,
  assessmentType,
  curriculumType,
  isBlueprintCourse = false,
  isSchoolLibrary = false,
  isLocked = false,
  hasLockedChildren,
  hasLockedAcademicTermChildren = false,
  isShared = false,
  hasClasses = false,
  hasStudents = false,
  hasSharingPermission = false,
  isPreviousAcademicYearSelected = false,
  hasEditAccess = true,
  isUniversalParentEnabled = false,
}) => {
  const moveToSectionOption = getMoveToSectionOption({
    t,
    itemType: type,
    itemDepth,
    maxChildDepth,
    sectionsCount,
    isUniversalParentEnabled,
  });

  const isGradingPeriodUnlocked = getIsGradingPeriodUnlocked(
    academicTerm,
    assessmentType
  );
  const isUpdateActionsLocked = !isBlueprintCourse && isLocked;
  const isDeleteBlockedByLockedChildren =
    !isBlueprintCourse && hasLockedChildren;

  const isProSchool = getIsProSchool();
  switch (type) {
    case "FOLDER": {
      return [
        ...(hasEditAccess
          ? [
              {
                key: "SECTION_RENAME",
                label: t("classMaterials:edit_section_title"),
                disabled: isUpdateActionsLocked,
                disabledTooltip: t(
                  "course:locked_item_edit_restriction_tooltip",
                  {
                    entity: t("common:folder"),
                  }
                ),
                icon: <PencilLineOutlined />,
                suffix: isUpdateActionsLocked ? (
                  <LockFilled variant="subtle" size="xxx-small" />
                ) : null,
              },
            ]
          : []),
        ...(isBlueprintCourse ||
        !isUnitFlowSharingEnabled ||
        !hasSharingPermission ||
        isPreviousAcademicYearSelected
          ? []
          : [
              {
                key: "SHARE_SETTINGS_SECTION",
                label: isShared
                  ? t("course:edit_share_settings")
                  : t("course:custom_share_settings"),
                icon: <ShareCurveOutlined />,
                disabled: !hasClasses || !hasStudents,
                disabledTooltip: getDisabledTooltipWithPrecedence({
                  t,
                  hasEditAccess: true, // assume edit access for sharing settings
                  hasClasses,
                  hasStudents,
                  entityType: "sharing",
                }),
              },
            ]),
        isExpanded
          ? {
              key: "COLLAPSE_ALL_SECTIONS",
              label: showExpandCollapseAllOption
                ? t("classMaterials:collapse_all_sections")
                : t("common:collapse"),
              icon: <CaretOpenedFilled />,
            }
          : {
              key: "EXPAND_ALL_SECTIONS",
              label: showExpandCollapseAllOption
                ? t("classMaterials:expand_all_sections")
                : t("common:expand"),
              icon: <CaretClosedFilled />,
            },
        ...(hasEditAccess ? moveToSectionOption : []),
        ...(hasEditAccess
          ? [
              {
                key: "SECTION_DELETE",
                label: t("common:delete"),
                icon: <DeleteOutlined />,
                disabled:
                  isUpdateActionsLocked ||
                  isDeleteBlockedByLockedChildren ||
                  hasLockedAcademicTermChildren,
                disabledTooltip: isUpdateActionsLocked
                  ? t("course:locked_item_delete_restriction_tooltip", {
                      entity: t("common:folder"),
                    })
                  : isDeleteBlockedByLockedChildren
                  ? t(
                      "course:locked_children_item_delete_restriction_tooltip",
                      {
                        entity: t("common:folder"),
                      }
                    )
                  : hasLockedAcademicTermChildren
                  ? t(
                      "course:you_cannot_delete_parent_since_it_has_locked_items",
                      {
                        parentEntityLabel: t("common:folder"),
                      }
                    )
                  : "",
                suffix:
                  isUpdateActionsLocked || isDeleteBlockedByLockedChildren ? (
                    <LockFilled variant="subtle" size="xxx-small" />
                  ) : null,
                isDestructive: true,
              },
            ]
          : []),
      ];
    }

    case "FILE": {
      const isLink = attachmentType === "LINK";
      const isNote = attachmentType === "NOTE";

      return [
        ...(hasEditAccess && isNote
          ? [
              {
                key: "NOTE_EDIT",
                label: t("common:edit"),
                icon: <PencilLineOutlined />,
                disabled: isUpdateActionsLocked,
                disabledTooltip: t(
                  "course:locked_item_edit_restriction_tooltip",
                  {
                    entity: t("common:note"),
                  }
                ),
                suffix: isUpdateActionsLocked ? (
                  <LockFilled variant="subtle" size="xxx-small" />
                ) : null,
              },
            ]
          : []),
        ...(hasEditAccess && isLink
          ? [
              {
                key: "EDIT_LINK",
                label: t("common:edit"),
                icon: <PencilLineOutlined />,
                disabled: isUpdateActionsLocked,
                disabledTooltip: t(
                  "course:locked_item_edit_restriction_tooltip",
                  {
                    entity: t("common:link"),
                  }
                ),
                suffix: isUpdateActionsLocked ? (
                  <LockFilled variant="subtle" size="xxx-small" />
                ) : null,
              },
            ]
          : []),
        ...(hasEditAccess && !isLink && !isNote
          ? [
              {
                key: "FILE_RENAME",
                label: t("common:rename"),
                icon: <PencilLineOutlined />,
                disabled: isUpdateActionsLocked,
                disabledTooltip: t(
                  "course:locked_item_edit_restriction_tooltip",
                  {
                    entity: t("common:resource_label"),
                  }
                ),
                suffix: isUpdateActionsLocked ? (
                  <LockFilled variant="subtle" size="xxx-small" />
                ) : null,
              },
            ]
          : []),
        {
          key: "FILE_VIEW",
          label: t("common:view"),
          icon: <EyeOutlined />,
        },
        ...(hasEditAccess
          ? [
              {
                key: "FILE_DUPLICATE",
                label: t("common:create_a_duplicate"),
                icon: <AddToLibraryOutlined />,
              },
            ]
          : []),
        ...(isBlueprintCourse ||
        !isUnitFlowSharingEnabled ||
        !hasSharingPermission ||
        isPreviousAcademicYearSelected
          ? []
          : [
              {
                key: "SHARE_SETTINGS_RESOURCE",
                label: isShared
                  ? t("course:edit_share_settings")
                  : t("course:custom_share_settings"),
                icon: <ShareCurveOutlined />,
                disabled: !hasClasses || !hasStudents,
                disabledTooltip: getDisabledTooltipWithPrecedence({
                  t,
                  hasEditAccess: true, // assume edit access for sharing settings
                  hasClasses,
                  hasStudents,
                  entityType: "sharing",
                }),
              },
            ]),
        ...(hasEditAccess ? moveToSectionOption : []),
        ...(!isLink && !isNote
          ? [
              {
                key: "FILE_DOWNLOAD",
                label: t("common:download"),
                icon: <DownloadOutlined />,
              },
            ]
          : []),
        ...(hasEditAccess
          ? [
              {
                key: "FILE_DELETE",
                label: t("common:delete"),
                icon: <DeleteOutlined />,
                disabled: isUpdateActionsLocked,
                disabledTooltip: t(
                  "course:locked_item_delete_restriction_tooltip",
                  {
                    entity: isNote
                      ? t("common:note")
                      : isLink
                      ? t("common:link")
                      : t("common:resource_label"),
                  }
                ),
                suffix: isUpdateActionsLocked ? (
                  <LockFilled variant="subtle" size="xxx-small" />
                ) : null,
                isDestructive: true,
              },
            ]
          : []),
      ];
    }

    case "ASSESSMENT": {
      return [
        ...(hasEditAccess
          ? [
              {
                key: "ASSESSMENT_EDIT",
                label: t("common:edit"),
                icon: <PencilLineOutlined />,
                disabled: isUpdateActionsLocked || !isGradingPeriodUnlocked,
                disabledTooltip: getDisabledTooltipForUpdations({
                  t,
                  isUpdateActionsLocked,
                  isGradingPeriodUnlocked,
                  entityLabel: assessmentLabel,
                  actionLabel: t("common:edit"),
                }),
                suffix: isUpdateActionsLocked ? (
                  <LockFilled variant="subtle" size="xxx-small" />
                ) : null,
              },
            ]
          : []),
        {
          key: "ASSESSMENT_VIEW",
          label: t("common:view"),
          icon: <EyeOutlined />,
        },
        ...(hasEditAccess
          ? [
              {
                key: "FILE_DUPLICATE",
                label: t("common:create_a_duplicate"),
                icon: <AddToLibraryOutlined />,
                disabled: !isGradingPeriodUnlocked,
                disabledTooltip: getDisabledTooltipForUpdations({
                  t,
                  isGradingPeriodUnlocked,
                  entityLabel: assessmentLabel,
                  actionLabel: t("common:duplicate"),
                }),
              },
            ]
          : []),
        // Only show assign to students for non-blueprint courses and non-Pro schools
        ...(isBlueprintCourse ||
        isProSchool ||
        !hasSharingPermission ||
        isPreviousAcademicYearSelected
          ? []
          : [
              {
                key: "ASSESSMENT_ASSIGN",
                label: isShared
                  ? t("classRoom:edit_assign_settings")
                  : t("common:assign"),
                icon: <UserTickOutlined />,
                disabled:
                  !hasClasses ||
                  !hasStudents ||
                  !isGradingPeriodUnlocked ||
                  !isTermsExists ||
                  isSchoolLibrary,
                disabledTooltip: getDisabledTooltipWithPrecedence({
                  t,
                  hasEditAccess: true, // assume edit access for assignment
                  hasClasses,
                  hasStudents,
                  isGradingPeriodUnlocked,
                  isTermExistsForCurrentLearningCourse: isTermsExists,
                  assessmentLabel,
                  entityType: "assigning",
                  actionLabel: isShared
                    ? t("classRoom:edit_assign_settings")
                    : t("common:assign"),
                }),
              },
            ]),
        ...(hasEditAccess ? moveToSectionOption : []),
        {
          icon: <PrintOutlined />,
          key: "ASSESSMENT_PRINT",
          label: t("common:print"),
        },
        ...(hasEditAccess
          ? [
              {
                key: "CHANGE_TASK",
                label: t("common:change_task_type"),
                disabled:
                  isUpdateActionsLocked ||
                  !isGradingPeriodUnlocked ||
                  _.includes(
                    [ASSESSMENT_TYPE_WORKSHEET, ASSESSMENT_TYPE_AI_TUTOR],
                    assessmentType
                  ),
                disabledTooltip: getDisabledTooltipForUpdations({
                  t,
                  isUpdateActionsLocked,
                  isGradingPeriodUnlocked,
                  entityLabel: assessmentLabel,
                  actionLabel: t("common:change_task_type"),
                  defaultTooltip: t(
                    "classRoom:change_task_type_dropdown_disabled_tooltip"
                  ),
                }),
                icon: <ReloadArrowOutlined />,
                shouldHide:
                  curriculumType === CURRICULUM_TYPE_PYP &&
                  !ACLStore.can(ENABLE_ASSESSMENTS_IN_PYP),
                suffix: isUpdateActionsLocked ? (
                  <LockFilled variant="subtle" size="xxx-small" />
                ) : null,
              },
              {
                key: "ASSESSMENT_DELETE",
                label: t("common:delete"),
                icon: <DeleteOutlined />,
                disabled: isUpdateActionsLocked || !isGradingPeriodUnlocked,
                disabledTooltip: getDisabledTooltipForUpdations({
                  t,
                  isUpdateActionsLocked,
                  isGradingPeriodUnlocked,
                  entityLabel: assessmentLabel,
                  actionLabel: t("common:delete"),
                  isDelete: true,
                }),
                isDestructive: true,
                suffix: isUpdateActionsLocked ? (
                  <LockFilled variant="subtle" size="xxx-small" />
                ) : null,
              },
            ]
          : []),
      ];
    }
  }

  return [];
};

export const getCardDropdownOptionsMemoized = getMemoizedLangFunction(
  getCardDropdownOptions
);

const getAssignmentModalData = ({ t }) => {
  return {
    mode: "create",
    creationMode: "ASSIGN_TO_CLASS",
    steps: [
      {
        label: t("common:edit_with_label", {
          label: t("common:post"),
        }),
        value: "ASSESSMENT_DETAILS",
      },
      {
        label: t("classRoom:post_settings"),
        value: "SETTINGS",
      },
    ],
    contentType: "ASSESSMENT",
    createHeader: t("common:assign_to_students"),
    updateUnitPlanDataInRedux: false,
  };
};

export const getAssignmentModalDataMemoized = getMemoizedLangFunction(
  getAssignmentModalData
);

export const getToastMsgForResourceUpdate = resourceType => {
  let msg = "";
  switch (resourceType) {
    case "LINK": {
      msg = "classMaterials:toast_msg_for_link_update";
      break;
    }
    case "NOTE": {
      msg = "classMaterials:toast_msg_for_note_update";
      break;
    }
    case "FILE": {
      msg = "classMaterials:toast_msg_for_resource_rename";
      break;
    }
    default: {
      msg = "classMaterials:toast_msg_for_section_rename";
      break;
    }
  }

  return {
    msg,
    type: "success",
    position: "toast-bottom-left",
  };
};

export const checkIsUnitFlowPage = url => {
  return _.includes(url, "/unitFlow");
};

export const getResourceData = ({ item, variables }) => {
  const {
    id,
    depth,
    parent,
    label,
    resourceType,
    childrenHierarchyInfo,
  } = item;
  const childFeedData = getUnitFlowItemFromCache({ id, variables });

  if (!_.isEmpty(childFeedData)) {
    return { ...childFeedData, depth, childrenHierarchyInfo };
  }

  return {
    id,
    label,
    displaySequence: 1,
    depth,
    resourceType,
    isSkeleton: true,
    item: null,
    parent,
    childrenHierarchyInfo: null,
    childrenV2: [],
    isRepeated: false,
    repeatedResources: [],
    lockingInfo: null,
    __typename: "LearningCourseFlow",
  };
};

const getUnitResourcesVariables = ({
  parentId: unitPlanId,
  learningCourseId,
  classId,
  searchText = "",
  first = 25,
  after = null,
}) => {
  const userInfo = getUserInfo();
  const userType = userInfo.user_type;
  const isUserTeacher = isUserTypeStaff({ userType });

  const parentId = isUserTeacher ? learningCourseId : classId;

  const filters = {
    attachmentTypes: [],
    searchText,
    itemTypes: ["FILE"],
    unitPlanId,
  };

  if (!isUserTeacher && !_.isEmpty(classId)) {
    filters.classIds = [classId];
  }

  return {
    parentId,
    parentType: isUserTeacher ? "LEARNING_COURSE" : "COURSE",
    filters,
    first,
    after,
  };
};

export const getUnitResourcesVariablesMemoized = getMemoizedFunction(
  getUnitResourcesVariables
);

export const convertToEntityFlowResource = ({
  resource,
  parent = null,
  unitFlowFeed,
}) => {
  if (!resource) return null;

  // default data for optimistic response
  const response = {
    childrenHierarchyInfo: null,
    childrenV2: [],
    parent,
    id: generateRandomId(),
    repeatedResources: [],
    displaySequence: 0,
    __typename: "LearningCourseFlow",
  };

  const isAttachmentResource = _.has(resource, "attachment");
  const item = {};
  if (isAttachmentResource) {
    item.id = _.get(resource, "id", "");
    item.__typename = "LearningCourseFlow";
    item.attachment = {};

    const attachment = _.get(resource, "attachment", {});
    item.attachment.id = _.get(attachment, "id", "");
    item.attachment.__typename = "Attachment";
    item.attachment.attachmentTitle = _.get(attachment, "title", "");
    item.attachment.attachmentType = _.get(attachment, "type", "");
    item.attachment.mimeType = _.get(attachment, "mimeType", null);
    item.attachment.name = _.get(attachment, "name", null);
    item.attachment.thumbUrl = _.get(attachment, "thumbUrl", null);
    item.attachment.signedUrl = _.get(attachment, "signedUrl", null);
    item.attachment.url = _.get(attachment, "url", null);

    response.resourceType = "FILE";

    const isNote = attachment?.type === "NOTE";
    const attachmentLabel = isNote
      ? _.get(attachment, "title", "--")
      : _.get(attachment, "name", "");
    response.label = _.get(resource, "label") ?? attachmentLabel;

    response.isRepeated = false;
  } else {
    item.__typename = "Assessment";
    item.type = ""; // cannot be fetched from org resource, change if causing error
    item.id = _.get(resource, "id", "");
    item.title = _.get(resource, "title", {});
    item.image = _.get(resource, "image", {});
    item.category = _.get(resource, "category", null);
    item.assessmentType = _.get(resource, "assessmentType", {});
    item.academicTerm = _.get(resource, "academicTerm", {});

    response.resourceType = "ASSESSMENT";
    response.label = _.get(resource, "title.value", "");
    response.isRepeated = _.some(
      unitFlowFeed?.edges,
      ({ node }) => node?.item?.id === item.id
    );
  }
  response.item = item;

  return response;
};

export const getModifiedFiltersForUnitFlowImport = filters => {
  const {
    itemTypes: attachmentTypes,
    resourceTypes,
    searchText,
    assessmentFilters,
  } = filters;

  const itemTypes = _.map(resourceTypes, type => {
    if (type === "ORGANIZATION_RESOURCE") return;
    return type;
  });

  return {
    entityFlowResourceFilter: {
      itemTypes,
      searchText,
      assessmentFilters,
      attachmentTypes,
    },
  };
};

export const getWrapperFieldName = () => {
  const userInfo = getUserInfo();
  const userType = userInfo.user_type;
  const isUserTeacher = isUserTypeStaff({ userType });

  return isUserTeacher
    ? "learningCourseFlowFeedWrapper"
    : "classFlowFeedWrapper";
};

export const getUnitFlowFeedEdges = feedData =>
  _.get(
    feedData,
    `node.${getWrapperFieldName()}.learningCourseFlowFeed.edges`,
    []
  );

export const getUnitFlowFeedTotalCount = feedData =>
  _.get(
    feedData,
    `node.${getWrapperFieldName()}.learningCourseFlowFeed.resourceBasedCount.totalCount`,
    0
  );

export const getUnitFlowOutlineFeed = data =>
  _.get(data, `${getWrapperFieldName()}.learningCourseFlowFeedOutline`, []);

export const getUnitResourcesFeed = data =>
  _.get(data, `node.${getWrapperFieldName()}.learningCourseFlowFeed`, []);

export const getImportFromUnitFlowInput = ({
  selectedInput,
  putAfterItemId,
  parentId,
  learningCourseId,
  selectedUnitLearningCourseId,
}) => {
  return {
    ..._.pick(selectedInput, ["includedIds"]),
    putAfterItemId, // For inline import - id of the item after which resources should be added or null
    parentId, // For inline import - folder id or null
    sourceId: selectedUnitLearningCourseId,
    destinationId: learningCourseId,
  };
};

export const getCreatedUnitFlowResourcesData = data =>
  _.get(data, "data.planner.createLearningCourseFlowResource");

// generate FilterOptions
export const generateFilterOptions = ({
  t,
  filters,
  addedByOptions,
  itemTypeOptions,
}) => {
  const options = {};
  const defaultSelectedOptions = {};

  const userInfo = getUserInfo();
  const userType = userInfo.user_type;
  const isUserTeacher = isUserTypeStaff({ userType });

  const populateOptions = ({
    filterName,
    filterOptions,
    title,
    shouldPopulate = true,
    isSingleSelect = false,
  }) => {
    if (!shouldPopulate) return;

    options[filterName] = {
      options: filterOptions,
      isSingleSelect,
      title: title,
    };

    const filtersValue = _.get(filters, filterName, []);

    if (filtersValue === "ALL") {
      defaultSelectedOptions[filterName] = _.flatMap(filterOptions, option => {
        const nestedOptions = option.options;
        if (nestedOptions) return _.map(nestedOptions, "value");
        return option.value;
      });
    } else {
      defaultSelectedOptions[filterName] = filtersValue;
    }
  };

  // Item type
  populateOptions({
    filterName: "selectedItemTypes",
    filterOptions: itemTypeOptions,
    title: t("classMaterials:item_types_label"),
  });

  // Added by
  populateOptions({
    filterName: "selectedAddedBy",
    filterOptions: addedByOptions,
    title: t("common:added_by"),
    isSingleSelect: true,
    shouldPopulate: isUserTeacher,
  });

  return { options, defaultSelectedOptions };
};

const getDisabledTooltipWithPrecedence = ({
  t,
  hasEditAccess,
  hasClasses,
  hasStudents,
  isGradingPeriodUnlocked,
  isTermExistsForCurrentLearningCourse,
  assessmentLabel,
  actionLabel,
  entityType = "sharing", // 'sharing' or 'assigning'
}) => {
  if (entityType === "assigning") {
    if (!isGradingPeriodUnlocked) {
      return t("course:you_cannot_with_label_since_locked", {
        actionLabel,
      });
    }
    if (!isTermExistsForCurrentLearningCourse) {
      return t(
        "classRoom:assign_to_students_no_grading_periods_tooltip_with_label_plural",
        {
          label: assessmentLabel,
        }
      );
    }
  }
  if (!hasClasses) {
    return t("course:no_classes_added");
  }
  if (!hasStudents) {
    return t("common:no_students_enrolled");
  }
  if (!hasEditAccess) {
    return t("course:no_creation_permission_tooltip");
  }
  return "";
};

const getDisabledTooltipForUpdations = ({
  t,
  isUpdateActionsLocked,
  isGradingPeriodUnlocked,
  entityLabel,
  actionLabel,
  defaultTooltip = "",
  isDelete = false,
}) => {
  if (isUpdateActionsLocked) {
    const actionLocale = isDelete
      ? "course:locked_item_delete_restriction_tooltip"
      : "course:locked_item_edit_restriction_tooltip";
    return t(actionLocale, {
      entity: entityLabel,
    });
  }

  if (!isGradingPeriodUnlocked) {
    return t("course:you_cannot_with_label_since_locked", {
      actionLabel,
    });
  }

  return defaultTooltip;
};

const getCardActionTooltipWithPrecedence = ({
  t,
  hasClasses,
  hasStudents,
  isGradingPeriodUnlocked = true,
  classText,
  entityText,
  tooltipType = "sharing", // 'sharing', 'assigning', 'file-sharing'
  isClassFilterApplied,
  returnAsObject = false, // for spread syntax in sharing buttons
}) => {
  const getTooltipText = () => {
    if (!hasClasses) {
      return t("course:no_classes_added");
    }
    if (!hasStudents) {
      return t("common:no_students_enrolled");
    }

    switch (tooltipType) {
      case "assigning":
        if (!isGradingPeriodUnlocked) {
          return t("course:you_cannot_with_label_since_locked", {
            actionLabel: t("common:assign"),
          });
        }
        return t("course:assign_to_all_with_default_settings_to_class", {
          entityText,
          classText,
        });

      case "file-sharing":
        return isClassFilterApplied
          ? t("course:share_with_all_students_of_class", { classText })
          : t("course:share_with_all_classes");

      case "sharing":
      default:
        return "";
    }
  };

  const tooltipText = getTooltipText();

  if (returnAsObject) {
    return tooltipText ? { tooltip: tooltipText } : {};
  }

  return tooltipText;
};

const getTeacherCourseCardActions = ({
  t,
  type,
  isShared,
  totalChildren,
  studentList,
  associatedClasses,
  isGradingPeriodUnlocked,
  isClassFilterApplied,
  selectedClassTitle,
}) => {
  const hasClasses = _.size(associatedClasses) > 0;
  const hasStudents = _.size(studentList) > 0;

  let classText = _.toLower(t("common:all_classes_plural"));
  let entityText = t("common:class_lowercase_other");

  if (isClassFilterApplied) {
    classText = selectedClassTitle;
    entityText = t("common:student_plural_lowercase");
  }

  switch (type) {
    case "FOLDER": {
      if (!isShared) {
        return [
          ...(totalChildren > 0
            ? [
                {
                  key: "SHARE_BUTTON",
                  type: "SplitButton",
                  action: "SECTION_SHARE_WITH_ALL_CONTENT",
                  isDisabled: !hasClasses || !hasStudents,
                  label: t("course:share_section_with_all_items"),
                  ...getCardActionTooltipWithPrecedence({
                    t,
                    hasClasses,
                    hasStudents,
                    tooltipType: "sharing",
                    returnAsObject: true,
                  }),
                  icon: <MarkAsReadOutlined />,
                  options: [
                    {
                      key: "SECTION_SHARE_WITH_ALL_CONTENT",
                      label: t("course:share_section_with_all_items"),
                      subText: t(
                        "course:share_section_with_all_items_to_class_subtext",
                        { classText }
                      ),
                      icon: <MarkAsReadOutlined />,
                    },
                    {
                      key: "SECTION_SHARE",
                      label: t("course:share_section_only"),
                      subText: t(
                        "course:share_section_without_content_to_class_subtext",
                        { classText }
                      ),
                      icon: <TickCircleOutlined />,
                    },
                  ],
                },
              ]
            : [
                {
                  key: "SECTION_SHARE_WITH_ALL_CONTENT",
                  type: "Button",
                  action: "SECTION_SHARE_WITH_ALL_CONTENT",
                  label: t("course:share_label", {
                    label: t("common:section"),
                  }),
                  isDisabled: !hasClasses || !hasStudents,
                  icon: <MarkAsReadOutlined />,
                  ...getCardActionTooltipWithPrecedence({
                    t,
                    hasClasses,
                    hasStudents,
                    tooltipType: "sharing",
                    returnAsObject: true,
                  }),
                },
              ]),
        ];
      }
      break;
    }

    case "ASSESSMENT": {
      if (!isShared) {
        return [
          {
            key: "QUICK_ASSIGN",
            type: "Button",
            action: "ASSESSMENT_QUICK_ASSIGN",
            label: t("classMaterials:quick_assign"),
            isDisabled: !hasClasses || !isGradingPeriodUnlocked || !hasStudents,
            icon: <TickCircleOpenOutlined />,
            tooltip: getCardActionTooltipWithPrecedence({
              t,
              hasClasses,
              hasStudents,
              isGradingPeriodUnlocked,
              classText,
              entityText,
              tooltipType: "assigning",
            }),
          },
        ];
      }
      break;
    }

    case "FILE": {
      if (!isShared) {
        return [
          {
            key: "QUICK_SHARE",
            type: "Button",
            action: "FILE_QUICK_SHARE",
            label: t("classMaterials:quick_share"),
            isDisabled: !hasClasses || !hasStudents,
            icon: <TickCircleOpenOutlined />,
            tooltip: getCardActionTooltipWithPrecedence({
              t,
              hasClasses,
              hasStudents,
              isGradingPeriodUnlocked: true, // file sharing doesn't depend on grading period
              classText,
              entityText: "",
              tooltipType: "file-sharing",
              isClassFilterApplied,
            }),
          },
        ];
      }
      break;
    }
  }

  return [];
};

const getBlueprintCourseCardActions = ({
  t,
  type,
  isLocked,
  assessmentLabel,
  attachmentType,
  isViewMode,
  hasConflict,
  hasChildren,
  hasLockedChildren,
  hasUnlockedChildren,
  syncStatus = {},
}) => {
  const isConflictBlockingLock =
    !getIsBlueprintSyncHistoryV2Enabled() && hasConflict;
  const showDisabled = isConflictBlockingLock && !isLocked && !isViewMode;

  let syncStatusActions = [];

  const { icon, tooltip, defaultIcon } =
    getSyncStatusCompData({ syncStatus, t, size: "xx-small" }) || {};

  syncStatusActions = !_.isEmpty(icon)
    ? [
        {
          key: "SYNCED_WITH_TEACHER_COURSE",
          type: "Icon",
          defaultIcon: defaultIcon,
          tooltip: tooltip,
          icon: icon,
        },
      ]
    : [];

  const isSyncAndLockAllowed = getIsSyncAndLockAllowed();

  const isRestricted = isViewMode || !isSyncAndLockAllowed;

  switch (type) {
    case "FOLDER": {
      const options = hasChildren
        ? getFolderLockingOptions({
            isLocked,
            hasUnlockedChildren,
            showDisabled,
            t,
          })
        : null;

      let tooltipLocale = isLocked
        ? "course:unlock_item_with_label"
        : "course:lock_item_with_label";

      if (isRestricted) {
        tooltipLocale = isLocked
          ? "course:unlock_item_with_label_read_only"
          : "course:lock_item_with_label_read_only";
      }

      const tooltipLabel = t(tooltipLocale, {
        label: t("common:folder"),
      });

      const buttonType = isRestricted
        ? "Icon"
        : hasChildren
        ? "IconDisclosure"
        : "IconButton";

      return [
        ...syncStatusActions,
        {
          key: isLocked
            ? LOCKING_ACTIONS.UNLOCK_SECTION_ONLY
            : LOCKING_ACTIONS.LOCK_SECTION_ONLY,
          type: buttonType,
          action: isLocked
            ? LOCKING_ACTIONS.UNLOCK_SECTION_ONLY
            : LOCKING_ACTIONS.LOCK_SECTION_ONLY,
          defaultIcon: isLocked ? <LockFilled /> : <LockOpenOutlined />,
          disabled: !hasChildren && showDisabled,
          tooltip:
            !hasChildren && showDisabled
              ? t("course:cannot_lock_in_conflict")
              : tooltipLabel,
          iconClasses: hasChildren ? null : classes.lockIconMargin,
          icon: isLocked ? <LockFilled /> : <LockOpenOutlined />,
          options,
        },
        ...(isRestricted
          ? []
          : [
              {
                type: "VerticalSeparator",
              },
            ]),
      ];
    }

    case "ASSESSMENT": {
      const buttonType = isRestricted ? "Icon" : "IconButton";

      if (!isLocked) {
        const tooltipLocale = isRestricted
          ? "course:lock_item_with_label_read_only"
          : "course:lock_item_with_label";

        const tooltipLabel = t(tooltipLocale, {
          label: assessmentLabel,
        });

        return [
          ...syncStatusActions,
          {
            key: LOCKING_ACTIONS.LOCK_ITEM,
            type: buttonType,
            iconClasses: isRestricted ? null : classes.lockIconMargin,
            action: LOCKING_ACTIONS.LOCK_ITEM,
            disabled: showDisabled,
            tooltip: showDisabled
              ? t("course:cannot_lock_in_conflict")
              : tooltipLabel,
            icon: <LockOpenOutlined />,
          },
          ...(isRestricted
            ? []
            : [
                {
                  type: "VerticalSeparator",
                },
              ]),
        ];
      } else {
        const tooltipLocale = isRestricted
          ? "course:unlock_item_with_label_read_only"
          : "course:unlock_item_with_label";
        const tooltipLabel = t(tooltipLocale, {
          label: assessmentLabel,
        });

        return [
          ...syncStatusActions,
          {
            key: LOCKING_ACTIONS.UNLOCK_ITEM,
            type: buttonType,
            iconClasses: isRestricted ? null : classes.lockIconMargin,
            action: LOCKING_ACTIONS.UNLOCK_ITEM,
            tooltip: tooltipLabel,
            icon: <LockFilled />,
          },
          ...(isRestricted
            ? []
            : [
                {
                  type: "VerticalSeparator",
                },
              ]),
        ];
      }
    }

    case "FILE": {
      let attachmentLabel = t("common:resource_label");
      if (attachmentType == "NOTE") {
        attachmentLabel = t("common:note");
      } else if (attachmentType == "LINK") {
        attachmentLabel = t("common:link");
      }

      const buttonType = isRestricted ? "Icon" : "IconButton";

      if (!isLocked) {
        const tooltipLocale = isRestricted
          ? "course:lock_item_with_label_read_only"
          : "course:lock_item_with_label";
        const tooltipLabel = t(tooltipLocale, {
          label: attachmentLabel,
        });
        return [
          ...syncStatusActions,
          {
            key: LOCKING_ACTIONS.LOCK_ITEM,
            type: buttonType,
            iconClasses: isRestricted ? null : classes.lockIconMargin,
            action: LOCKING_ACTIONS.LOCK_ITEM,
            disabled: showDisabled,
            tooltip: showDisabled
              ? t("course:cannot_lock_in_conflict")
              : tooltipLabel,
            icon: <LockOpenOutlined />,
          },
          ...(isRestricted
            ? []
            : [
                {
                  type: "VerticalSeparator",
                },
              ]),
        ];
      } else {
        const tooltipLocale = isRestricted
          ? "course:unlock_item_with_label_read_only"
          : "course:unlock_item_with_label";
        const tooltipLabel = t(tooltipLocale, {
          label: attachmentLabel,
        });

        return [
          ...syncStatusActions,
          {
            key: LOCKING_ACTIONS.UNLOCK_ITEM,
            type: buttonType,
            iconClasses: isRestricted ? null : classes.lockIconMargin,
            action: LOCKING_ACTIONS.UNLOCK_ITEM,
            tooltip: tooltipLabel,
            icon: <LockFilled />,
          },
          ...(isRestricted
            ? []
            : [
                {
                  type: "VerticalSeparator",
                },
              ]),
        ];
      }
    }
  }

  return [];
};

const getCardActions = ({
  t,
  type,
  isShared,
  associatedClasses,
  isGradingPeriodUnlocked,
  totalChildren = 0,
  isBlueprintCourse = false,
  isTeacherCourseAssociationEnabled = false,
  isLocked = false,
  isClassFilterApplied = false,
  isUnitFlowSharingEnabled = true,
  selectedClassTitle = "",
  assessmentLabel,
  attachmentType,
  isViewMode = false,
  studentList = [],
  hasConflict = false,
  hasChildren = false,
  hasLockedChildren = false,
  hasUnlockedChildren = false,
  syncStatus = {},
  isPreviousAcademicYearSelected = false,
}) => {
  if (isBlueprintCourse) {
    if (!isTeacherCourseAssociationEnabled) {
      // If teacher course association is not enabled, return empty actions
      return [];
    }

    return getBlueprintCourseCardActions({
      t,
      type,
      isLocked,
      assessmentLabel,
      attachmentType,
      isViewMode,
      hasConflict,
      hasChildren,
      hasLockedChildren,
      hasUnlockedChildren,
      syncStatus,
    });
  } else {
    if (
      !isUnitFlowSharingEnabled ||
      isViewMode ||
      isPreviousAcademicYearSelected
    ) {
      return [];
    }

    return getTeacherCourseCardActions({
      t,
      type,
      isShared,
      totalChildren,
      studentList,
      associatedClasses,
      isGradingPeriodUnlocked,
      isClassFilterApplied,
      selectedClassTitle,
    });
  }
};

export const getCardActionsMemoized = getMemoizedLangFunction(
  getCardActions,
  ({ associatedClasses, studentList, ...restParams }) =>
    JSON.stringify({
      ...restParams,
      associatedClasses: _.map(associatedClasses, "id"),
      studentList: _.map(studentList, "id"),
    })
);

// Sharing constants and functions
export const SHARE_ACTIONS = {
  SHARE: "SHARE",
  UNSHARE: "UNSHARE",
};

export const SHARING_STATES = {
  DRAFT: "DRAFT",
  PUBLISHED: "PUBLISHED",
};

export const getIsResourceShared = ({ orgResource }) => {
  if (_.isEmpty(orgResource)) return null;
  return (
    orgResource.state === SHARING_STATES.PUBLISHED ||
    !_.isEmpty(orgResource.sharedDetails)
  );
};

const getSharedSettingDiff = ({
  itemSharedDetails = [],
  sharedDetails = [],
}) => {
  const diff = {
    classes: [],
    students: {},
  };

  let isSubset = true;

  const itemSharingMapping = _.reduce(
    itemSharedDetails,
    (acc, cls) => {
      acc[cls.class.id] = _.map(cls.students, "id");
      return acc;
    },
    {}
  );

  const sharingMapping = _.reduce(
    sharedDetails,
    (acc, cls) => {
      acc[cls.class.id] = _.map(cls.students, "id");
      return acc;
    },
    {}
  );

  // Check if itemSharingMapping is subset of sharingMapping
  for (const key in itemSharingMapping) {
    if (!_.has(sharingMapping, key)) {
      // Class is not present in sharingMapping
      diff.classes.push(key);
      isSubset = false;
    } else {
      const itemStudents = itemSharingMapping[key];
      const students = sharingMapping[key];
      const difference = _.difference(itemStudents, students);
      if (!_.isEmpty(difference)) {
        // Students are not same
        diff.students[key] = difference;
        isSubset = false;
      }
    }
  }

  return { isSubset, diff };
};

export const getIsResourceSharedRecursivelyWithSameSettings = ({
  sharedDetails: _sharedDetails = [],
  orgResource,
  items,
  isParentCheck = false,
}) => {
  const isShared = getIsResourceShared({ orgResource });
  const resourceSharedDetails = _.get(orgResource, "sharedDetails", []);
  const sharedDetails = _.isEmpty(_sharedDetails)
    ? resourceSharedDetails
    : _sharedDetails;

  let isSharedWithSameSettings = true;
  let diffObj = {};

  if (!_.isEmpty(_sharedDetails)) {
    // While checking share details wrt parent, parent's sharedDetails will be the superset
    if (isParentCheck) {
      diffObj = getSharedSettingDiff({
        itemSharedDetails: sharedDetails,
        sharedDetails: resourceSharedDetails,
      });

      diffObj.parentType = _.get(orgResource, "resourceType", "FOLDER");
    } else {
      diffObj = getSharedSettingDiff({
        itemSharedDetails: resourceSharedDetails,
        sharedDetails,
      });
    }

    isSharedWithSameSettings = diffObj.isSubset;
  }

  if (isShared && isSharedWithSameSettings) {
    const parent = _.find(items, { id: _.get(orgResource, "parent.id") });
    if (parent) {
      return getIsResourceSharedRecursivelyWithSameSettings({
        orgResource: parent,
        sharedDetails,
        items,
        isParentCheck: true,
      });
    } else return true;
  } else
    return {
      ...diffObj,
      isSubset: false,
      isVisible: false,
    };
};
